/**
 * Tier 3 of the design system: a component's LOOK as data (DESIGN_SYSTEM.md §4).
 *
 * A member writes its look once (`<Member>/look.ts`) with `defineVariants`: named slots, a base,
 * variant axes, compound entries, interaction states and defaults, each a function of the theme. Its
 * body only asks for the resolved styles. The resolution order is fixed: base, each axis in the order
 * the spec lists it, matching compounds, then the states `hovered`, `focused`, `pressed`, `disabled`
 * (so disabled wins). Each slot is a shallow merge.
 *
 * Slot convention (§4.2): `root` is the element react-native-web turns into a `<button>` or an
 * `<input>`, so it takes LAYOUT ONLY; `surface` takes the paint; `label` and `icon` are content.
 * `hostSafety.test.ts` holds every registered look to it.
 */
import { StyleSheet } from "react-native";
import type { TextStyle } from "react-native";

import type { Theme } from "./theme";

export type SlotStyle = TextStyle;
export type SlotStyles<S extends string> = Partial<Record<S, SlotStyle>>;

export const STATES = ["hovered", "focused", "pressed", "disabled"] as const;
export type LookState = Partial<Record<(typeof STATES)[number], boolean>>;

/** `{ true: … }` / `{ false: … }` axes take a boolean; every other axis takes one of its keys. */
type ValueOf<K> = K extends "true" | "false" ? boolean : K;
type Axes<S extends string> = Record<string, Record<string, (t: Theme) => SlotStyles<S>>>;
export type Picks<V> = { [A in keyof V]?: ValueOf<keyof V[A]> };
/** the picks a given look takes, for a component generic over its look */
export type PicksOf<L> = L extends LookSpec<any, infer V> ? Picks<V> : never;

export interface LookSpec<S extends string, V extends Axes<S>> {
  slots: readonly S[];
  base?: (t: Theme) => SlotStyles<S>;
  variants?: V;
  compound?: ReadonlyArray<{ when: Picks<V>; style: (t: Theme) => SlotStyles<S> }>;
  states?: Partial<Record<(typeof STATES)[number], (t: Theme, picks: Picks<V>) => SlotStyles<S>>>;
  defaults?: Picks<V>;
}

export type Resolved<S extends string> = Record<S, SlotStyle>;

/** Identity, for inference: the variant names a caller may pass come from the spec itself. */
export function defineVariants<S extends string, V extends Axes<S>>(spec: LookSpec<S, V>): LookSpec<S, V> {
  return spec;
}

/** The picks with the spec's defaults filled in, in the spec's axis order. */
function withDefaults<S extends string, V extends Axes<S>>(spec: LookSpec<S, V>, picks: Picks<V>): Picks<V> {
  const out: Record<string, unknown> = {};
  for (const axis of Object.keys(spec.variants ?? {})) {
    const value = (picks as Record<string, unknown>)[axis] ?? (spec.defaults as Record<string, unknown> | undefined)?.[axis];
    if (value !== undefined) out[axis] = value;
  }
  return out as Picks<V>;
}

/** PURE: the styles of every slot for one theme, set of picks and state. Testable as data. */
export function resolveVariants<S extends string, V extends Axes<S>>(
  spec: LookSpec<S, V>,
  theme: Theme,
  picks: Picks<V> = {},
  state: LookState = {},
): Resolved<S> {
  const chosen = withDefaults(spec, picks);
  const out = Object.fromEntries(spec.slots.map((slot) => [slot, {}])) as Record<S, SlotStyle>;
  const apply = (styles: SlotStyles<S> | undefined) => {
    for (const slot of Object.keys(styles ?? {}) as S[]) Object.assign(out[slot], styles![slot]);
  };
  apply(spec.base?.(theme));
  for (const [axis, values] of Object.entries(spec.variants ?? {})) {
    const value = (chosen as Record<string, unknown>)[axis];
    if (value !== undefined) apply(values[String(value)]?.(theme));
  }
  for (const entry of spec.compound ?? [])
    if (Object.entries(entry.when).every(([axis, value]) => (chosen as Record<string, unknown>)[axis] === value))
      apply(entry.style(theme));
  for (const name of STATES) if (state[name]) apply(spec.states?.[name]?.(theme, chosen));
  return out;
}

const cache = new WeakMap<object, Map<string, unknown>>();

/**
 * `resolveVariants`, made once per theme, picks and state and shared after that (§4.4): the same
 * object every time, so nothing is allocated per render and a memoised child is not re-rendered, and
 * made with `StyleSheet.create`, so react-native-web gives it atomic classes rather than inline styles.
 */
export function lookFor<S extends string, V extends Axes<S>>(
  spec: LookSpec<S, V>,
  theme: Theme,
  picks: Picks<V> = {},
  state: LookState = {},
): Resolved<S> {
  let entries = cache.get(spec);
  if (!entries) cache.set(spec, (entries = new Map()));
  const chosen = withDefaults(spec, picks) as Record<string, unknown>;
  const key = `${theme.id}|${Object.keys(chosen).map((axis) => `${axis}=${chosen[axis]}`).join(",")}|${STATES.map((s) => (state[s] ? 1 : 0)).join("")}`;
  let hit = entries.get(key) as Resolved<S> | undefined;
  if (!hit) entries.set(key, (hit = StyleSheet.create(resolveVariants(spec, theme, picks, state)) as Resolved<S>));
  return hit;
}
